import React from 'react'
import { makeStyles, useTheme } from '@mui/styles'
import { useMediaQuery } from '@mui/material'

import lang from '@/lang'
import getPhoneHref from '../_helpers/getPhoneHref'
import ThemeInterface from '@/theme-interface'
import { brandOutlineButton } from '../_helpers/brandButtons'
import { brandHeading } from '@/utils/brand-typography'
import { useAppSelector } from '@/redux/store'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import { CancelButton, SquareIconButton } from '@/modules/base/components/buttons'
import { SIZES, VARIANTS } from '@/modules/base/components/buttons/square-icon-button/square-icon-button'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import OrgamaxLogo from '@assets/images/logo/svg/logo-brand-dark.svg'
import getCheckoutTitle from '../_helpers/getCheckoutTitle'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    // stays visible while scrolling, so leaving and help are always at hand
    position: 'sticky',
    top: 0,
    zIndex: 2,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 24,
    padding: '16px 32px',
    borderBottom: `1px solid ${theme.palette.brand.divider}`,
    background: theme.palette.background.paper,
    [theme.breakpoints.down(768)]: {
      padding: '12px 16px',
      gap: 12,
    },
    fontSize: theme.brandTypography.text.regular.fontSize,
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minWidth: 0,
  },
  titleWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    minWidth: 0,
  },
  // left aligned above the title - stretched over the column the svg would centre the logo
  logo: {
    alignSelf: 'flex-start',
  },
  title: {
    margin: 0,
    ...brandHeading(theme, 'h6', 'semibold'),
    color: theme.palette.brand.text.heading,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
  },
  contact: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 4,
    paddingRight: 24,
    borderRight: `1px solid ${theme.palette.brand.divider}`,
    color: theme.palette.brand.text.primary,
    '&:last-child': {
      paddingRight: 0,
      borderRight: 'none',
    },
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  contactTitle: {
    fontWeight: theme.brandTypography.weights.semibold,
    color: theme.palette.brand.text.heading,
  },
  contactNumber: {
    marginLeft: 8,
    color: theme.palette.brand.main,
    fontWeight: theme.brandTypography.weights.normal,
  },
  businessHours: {
    color: theme.palette.brand.text.primary,
  },
  cancelButton: {
    ...brandOutlineButton(theme),
  },
}))

interface HeaderProps {
  // no cancel button without it, e.g. while the order is processed
  onCancel?: () => void
  // mobile: the button that opens "Deine Auswahl"
  selectionAction?: React.ReactNode
}

const Header = ({ onCancel, selectionAction }: HeaderProps) => {
  const classes = useStyles()
  const theme = useTheme<ThemeInterface>()
  const isMobile = useMediaQuery(theme.breakpoints.down(768))
  const showCancel = Boolean(onCancel)
  const { activeStep, orderCompleted } = useAppSelector(state => state.upgrade.wizard)
  const texts = lang.upgrade.wizard
  const { contactInfo } = texts.footer

  const title = getCheckoutTitle(activeStep, orderCompleted)

  return (
    <header className={classes.root}>
      <div className={classes.brand}>
        {isMobile && showCancel && !orderCompleted && (
          <SquareIconButton
            icon={CloseRoundedIcon}
            size={SIZES.medium}
            variant={VARIANTS.icon}
            ariaLabel={lang.cancelButtonLabel}
            dataQsId='checkout-cancel'
            onClick={onCancel}
          />
        )}
        <div className={classes.titleWrapper}>
          <SVGIcon svg={OrgamaxLogo} height='24px' width='108px' className={classes.logo} />
          {/* mobile: the title moves into the page, above the stepper */}
          {!isMobile && <h1 className={classes.title} data-qs-id='checkout-header-title'>{title}</h1>}
        </div>
      </div>
      <div className={classes.actions}>
        <div className={classes.contact}>
          <div>
            <span className={classes.contactTitle}>{contactInfo.title}</span>
            <a className={classes.contactNumber} href={getPhoneHref(contactInfo.number)} data-qs-id='checkout-header-phone'>{contactInfo.number}</a>
          </div>
          <span className={classes.businessHours}>{contactInfo.businessHours}</span>
        </div>
        {!isMobile && showCancel && !orderCompleted && (
          <CancelButton
            className={classes.cancelButton}
            label={lang.cancelButtonLabel}
            dataQsId='checkout-cancel'
            onClick={onCancel}
          />
        )}
        {selectionAction}
      </div>
    </header>
  )
}

export default Header
